import {
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  RadioGroup,
  Radio,
  CircularProgress,
  Switch,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Print from '@mui/icons-material/Print'
import PrintDisabled from '@mui/icons-material/PrintDisabled'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  DRUG_ADVICE_TABLE_TYPES_ARRAY,
  VITALS_DISPLAY_FORMAT_OPTIONS,
  VITALS_DISPLAY_FORMATS,
  LIST_ITEM_SEPARATOR_OPTIONS,
  DEFAULT_LIST_ITEM_SEPARATOR,
} from 'src/consumer/constants'
import { useEmrSettings } from 'src/mentor/components/MentorSettingsPage/hooks/useEmrSettings'
import EmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/components/EmrSectionsEditorDialog'
import useEmrSectionsEditorDialog from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useEmrSectionsEditorDialog'
import { EMR_SECTION_IDS, getCanonicalEmrSectionStyleOverrideKey, isDrugAdviceEmrSection } from '../utils/emrSectionIds'
import { getEmrSectionIcon } from '../utils/sectionIcons'
import SignatureDetailsSection from './SignatureDetailsSection'
import { getDefaultSectionStyle } from 'src/mentor/components/PrescriptionMakingPage/printComponents/utils/sectionColorPalette'
import PrescriptionTemplateBodySectionsStylesOverrideHandler from './PrescriptionTemplateBodySectionsStylesOverrideHandler'

const PRESCRIPTION_REPORT_TYPE = 'prescription'

function getSubsectionsForItem(item, sectionSubsectionsMap) {
  const sectionCode = item?.defCode || item?.id
  if (sectionSubsectionsMap?.[sectionCode]) return sectionSubsectionsMap[sectionCode]
  if (Array.isArray(item?.subSections)) return item.subSections
  if (Array.isArray(item?.emrSectionResponse?.section?.subSections)) {
    return item.emrSectionResponse.section.subSections
  }
  return []
}

export default function PrescriptionBodySection({
  prescriptionTemplate,
  prescriptionTemplateHandlers,
  availableEmrSections = [],
  mentorProfileData,
  refreshEmrSections,
  emrSectionsApiSignature = '/api/secure/practitioner/clinical/emr-settings/emr-sections',
  emrSettingsSaveApiSignature = '/api/secure/practitioner/emr-settings/emr-sections/save',
  onLiveEmrSectionsChange,
  inheritedStyleOverrides,
  disabled = false,
}) {
  const {
    open: emrSectionsEditorDialogOpen,
    handleOpen: emrSectionsEditorDialogHandleOpen,
    handleClose: emrSectionsEditorDialogHandleClose,
  } = useEmrSectionsEditorDialog()

  const { handleBodyBooleanChange, handleBodyValueChange, createSetOverrideData } =
    prescriptionTemplateHandlers

  // The per-section enable / print toggles + sub-sections are server-backed EMR settings.
  // We drive them with the same hook the full editor uses, scoped to the prescription
  // report type, so each section's accordion can own its toggles and we persist via Save.
  const {
    emrSettingsMap,
    loading: emrLoading,
    saveLoading,
    fetchEmrSettings,
    saveEmrSettings,
    toggleSectionForReportType,
    togglePrintableForReportType,
    sectionSubsectionsMap,
    toggleSectionExpansion,
    toggleSubsectionExclusion,
    toggleSubsectionPrintable,
    fetchSectionEffectiveLayout,
  } = useEmrSettings({
    fetchApiSignature: emrSectionsApiSignature,
    saveApiSignature: emrSettingsSaveApiSignature,
    approvedServicesMap: mentorProfileData?.teacherUnEditableFields?.servicesApproved,
    fetchCategories: false,
  })

  useEffect(() => {
    fetchEmrSettings()
  }, [fetchEmrSettings])

  const emrSections = useMemo(
    () => emrSettingsMap?.[PRESCRIPTION_REPORT_TYPE]?.emrSections || [],
    [emrSettingsMap],
  )
  const extraExcludedSubSectionsInSections =
    emrSettingsMap?.[PRESCRIPTION_REPORT_TYPE]?.extraExcludedSubSectionsInSections || {}

  // Push the live (unsaved) section enable/print state up so the right-side preview can
  // reflect editor/print toggles immediately, before the practitioner clicks Save.
  useEffect(() => {
    if (onLiveEmrSectionsChange && emrSections.length > 0) {
      onLiveEmrSectionsChange(emrSections)
    }
  }, [emrSections, onLiveEmrSectionsChange])

  // Fall back to the read-only redux sections (no toggles) only until the settings load.
  const sectionsToRender = emrSections.length > 0 ? emrSections : availableEmrSections

  const [savingSections, setSavingSections] = useState(false)
  const handleSaveSections = async () => {
    if (savingSections) return
    setSavingSections(true)
    try {
      const saveResult = await saveEmrSettings()
      if (saveResult) {
        refreshEmrSections?.(saveResult)
        toast.success('Sections updated')
      } else {
        toast.error('Could not save section settings — please try again')
      }
    } finally {
      setSavingSections(false)
    }
  }

  // Medication table layout style (per-template). Empty → Default Table.
  const selectedDrugAdviceTableType =
    prescriptionTemplate?.body?.drugAdviceTablePrintType || 'default'

  // Drug-advice-specific controls live inside the Drug Advice (Rx) section accordion.
  const drugAdviceSectionControls = (
    <>
      <FormControlLabel
        control={
          <Checkbox
            id="hideEmptyDrugAdviceColumns"
            size="small"
            checked={prescriptionTemplate?.body?.hideEmptyDrugAdviceColumns || false}
            onChange={handleBodyBooleanChange}
            disabled={disabled}
          />
        }
        label={
          <Typography variant="body2" fontWeight={500}>
            Hide empty drug advice columns
          </Typography>
        }
        sx={{ mb: 1 }}
      />
      <FormControl component="fieldset" disabled={disabled} sx={{ display: 'block', mb: 1.5 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary" gutterBottom>
          Medication table style
        </Typography>
        <RadioGroup
          name="drugAdviceTablePrintType"
          value={selectedDrugAdviceTableType}
          onChange={(e) => handleBodyValueChange('drugAdviceTablePrintType', e.target.value)}
        >
          {DRUG_ADVICE_TABLE_TYPES_ARRAY.map((option) => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              control={<Radio size="small" />}
              label={
                <span>
                  <Typography variant="body2" component="span" fontWeight={500}>
                    {option.title}
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" component="div" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </span>
              }
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </>
  )

  const booleanFields = [
    {
      id: 'showPrescriptionQR',
      label: 'Show Prescription QR Code In Next Visit Column',
      value: prescriptionTemplate?.body?.showPrescriptionQR !== false, // Default true
    },
  ]

  const selectedVitalsFormat =
    prescriptionTemplate?.body?.vitalsDisplayFormat || VITALS_DISPLAY_FORMATS.list
  const selectedSeparator =
    prescriptionTemplate?.body?.listItemSeparator || DEFAULT_LIST_ITEM_SEPARATOR

  // Vitals-section controls (display format + bold name) live inside the Vitals accordion.
  const vitalsSectionControls = (
    <>
      <FormControlLabel
        control={
          <Checkbox
            id="vitalsNameBold"
            size="small"
            checked={prescriptionTemplate?.body?.vitalsNameBold !== false}
            onChange={handleBodyBooleanChange}
            disabled={disabled}
          />
        }
        label={
          <Typography variant="body2" fontWeight={500}>
            Make the vital name bold
          </Typography>
        }
        sx={{ mb: 1 }}
      />
      <FormControl component="fieldset" disabled={disabled} sx={{ display: 'block', mb: 1 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary" gutterBottom>
          Vitals display format
        </Typography>
        <RadioGroup
          name="vitalsDisplayFormat"
          value={selectedVitalsFormat}
          onChange={(e) => handleBodyValueChange('vitalsDisplayFormat', e.target.value)}
        >
          {VITALS_DISPLAY_FORMAT_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              control={<Radio size="small" />}
              label={
                <span>
                  <Typography variant="body2" component="span" fontWeight={500}>
                    {option.title}
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" component="div" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </span>
              }
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </>
  )

  const handleAccordionExpand = async (item, isExpanded) => {
    const sectionCode = item?.defCode || item?.id
    toggleSectionExpansion?.(item?.id, sectionCode, PRESCRIPTION_REPORT_TYPE)
    if (isExpanded && sectionCode) {
      const existing = getSubsectionsForItem(item, sectionSubsectionsMap)
      if (existing.length === 0) {
        try {
          await fetchSectionEffectiveLayout?.(sectionCode, PRESCRIPTION_REPORT_TYPE)
        } catch (e) {
          // non-fatal: sub-sections just won't show
        }
      }
    }
  }

  return (
    <>
      {/* Body configuration toggles */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
          Body configuration
        </Typography>
      </Grid>

      {booleanFields.map((field) => (
        <Grid item xs={12} key={field.id}>
          <FormControlLabel
            control={
              <Checkbox
                id={field.id}
                checked={field.value}
                onChange={handleBodyBooleanChange}
                disabled={disabled}
              />
            }
            label={field.label}
          />
        </Grid>
      ))}

      {/* Prescription-wide separator for list-style key/value rendering (e.g. Vitals list). */}
      <Grid item xs={12}>
        <FormControl component="fieldset" disabled={disabled} sx={{ display: 'block' }}>
          <Typography variant="caption" fontWeight={600} color="text.primary" gutterBottom>
            List separator (used wherever items are shown inline)
          </Typography>
          <RadioGroup
            row
            name="listItemSeparator"
            value={selectedSeparator}
            onChange={(e) => handleBodyValueChange('listItemSeparator', e.target.value)}
          >
            {LIST_ITEM_SEPARATOR_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{option.title}</Typography>}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Unified Sections list: each section owns its enable / print toggles (always
          visible in the header) and, when expanded, its sub-sections + print-style
          overrides. */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            mb: 0.5,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            Sections
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="text"
              size="small"
              onClick={emrSectionsEditorDialogHandleOpen}
              sx={{ textTransform: 'none' }}
            >
              Advanced (reorder)
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveSections}
              disabled={savingSections || saveLoading || disabled}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Save sections
              {(savingSections || saveLoading) && (
                <CircularProgress size={14} sx={{ ml: 1, color: 'inherit' }} />
              )}
            </Button>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Toggle each section in the editor and in print, then Save. Expand a section for its
          sub-sections and print styling.
        </Typography>
        {emrLoading && emrSections.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Grid>

      {sectionsToRender.map((emrSection) => {
        const styleOverrideKey = getCanonicalEmrSectionStyleOverrideKey(emrSection)
        const sectionCode = emrSection?.defCode || emrSection?.id
        const subsections = getSubsectionsForItem(emrSection, sectionSubsectionsMap)
        const sectionExclusions =
          (extraExcludedSubSectionsInSections &&
            extraExcludedSubSectionsInSections[sectionCode]) ||
          []
        const isScreeningReportSection = emrSection?.id === 'screeningReportData'
        // Sections only carry the toggle state once the EMR settings have loaded.
        const hasToggleState = emrSection?.selected !== undefined

        return (
          <Grid item xs={12} key={emrSection.id}>
            <Accordion
              slotProps={{ transition: { unmountOnExit: true } }}
              onChange={(_, expanded) => handleAccordionExpand(emrSection, expanded)}
              sx={{
                borderRadius: '10px !important',
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
                '& + &': { mt: 1.5 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', pr: 1 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', '& svg': { fontSize: 18 } }}>
                    {getEmrSectionIcon(emrSection)}
                  </Box>
                  <Typography variant="body2" fontWeight={500} sx={{ minWidth: 0 }}>
                    {emrSection.title || emrSection.id}
                  </Typography>
                </Box>
                {/* Editor + print toggles, accessible without expanding. */}
                {hasToggleState && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    <Tooltip title={emrSection?.selected ? 'Shown in editor' : 'Hidden from editor'}>
                      <span>
                        <Switch
                          size="small"
                          checked={emrSection?.selected ?? false}
                          disabled={disabled || isScreeningReportSection}
                          onChange={(e) =>
                            toggleSectionForReportType(
                              PRESCRIPTION_REPORT_TYPE,
                              emrSection,
                              e.target.checked,
                            )
                          }
                        />
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={emrSection?.printable ? 'Shown in print (click to hide)' : 'Hidden from print (click to show)'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={disabled || !emrSection?.selected}
                          onClick={() => togglePrintableForReportType(PRESCRIPTION_REPORT_TYPE, emrSection)}
                          sx={{ color: 'text.primary' }}
                        >
                          {emrSection?.printable ? (
                            <Print fontSize="small" />
                          ) : (
                            <PrintDisabled fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails>
                {/* Sub-sections for this section */}
                {subsections.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      Sub-sections
                    </Typography>
                    {subsections.map((subsection) => {
                      const subsectionUid = subsection?.uid
                      const subsectionCode = subsection?.code
                      const inExclusionMap =
                        sectionExclusions.includes(subsectionUid) ||
                        (subsectionCode && sectionExclusions.includes(subsectionCode))
                      const isExcluded =
                        inExclusionMap ||
                        (subsection?.selected !== undefined ? !subsection.selected : false)
                      return (
                        <Box
                          key={subsectionUid || subsectionCode}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                            {subsection?.title || subsectionUid}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Switch
                              size="small"
                              checked={!isExcluded}
                              disabled={disabled}
                              onChange={(e) =>
                                toggleSubsectionExclusion(
                                  PRESCRIPTION_REPORT_TYPE,
                                  emrSection?.id,
                                  subsectionCode || subsectionUid,
                                  !e.target.checked,
                                )
                              }
                            />
                            {toggleSubsectionPrintable && (
                              <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={() =>
                                  toggleSubsectionPrintable(
                                    PRESCRIPTION_REPORT_TYPE,
                                    emrSection?.id,
                                    subsectionCode || subsectionUid,
                                  )
                                }
                                sx={{ color: 'text.primary' }}
                              >
                                {subsection?.printable !== false ? (
                                  <Print fontSize="small" />
                                ) : (
                                  <PrintDisabled fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                )}

                {/* The Drug Advice (Rx) section exposes its own controls. */}
                {isDrugAdviceEmrSection(emrSection) && drugAdviceSectionControls}

                {/* The Vitals section exposes display-format controls. */}
                {emrSection?.id === EMR_SECTION_IDS.PATIENT_VITALS && vitalsSectionControls}

                {/* The Signature section owns the signature-specific config (upload image,
                    position left/right, show practitioner name + case, license details, lines
                    below the signature). */}
                {emrSection?.id === EMR_SECTION_IDS.SIGNATURE && (
                  <SignatureDetailsSection
                    prescriptionTemplate={prescriptionTemplate}
                    handleChangeSignatureDetails={prescriptionTemplateHandlers.handleChangeSignatureDetails}
                    handleSignatureRichTextChange={prescriptionTemplateHandlers.handleSignatureRichTextChange}
                    disabled={disabled}
                  />
                )}

                {/* Print-style overrides for this section. The Signature section has no list
                    items, so the item-formatting toggles (capitalize/bold/bullet) don't apply
                    there — hide them to avoid the impression they're broken. */}
                <PrescriptionTemplateBodySectionsStylesOverrideHandler
                  emrSectionId={styleOverrideKey}
                  setPrescriptionTemplate={prescriptionTemplateHandlers.setPrescriptionTemplate}
                  overrideData={
                    prescriptionTemplate?.body?.styleOverridesForPrintedEmrSections?.[styleOverrideKey] || {}
                  }
                  inheritedStyle={inheritedStyleOverrides?.[styleOverrideKey]}
                  // Lowest-priority fallback = the colour the preview actually renders for this
                  // section in colored mode (the professional palette), so the pickers match the preview.
                  defaultStyle={getDefaultSectionStyle(styleOverrideKey, 'colored')}
                  hideItemFormatting={emrSection?.id === EMR_SECTION_IDS.SIGNATURE}
                  setOverrideData={createSetOverrideData(styleOverrideKey)}
                  emrSectionName={emrSection.title || emrSection.id}
                  disabled={disabled}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
        )
      })}

      {/* EMR Sections Editor Dialog (advanced: reorder, etc.) */}
      <EmrSectionsEditorDialog
        hideReordering
        open={emrSectionsEditorDialogOpen}
        handleClose={emrSectionsEditorDialogHandleClose}
        refreshEmrSections={refreshEmrSections}
        mentorProfileData={mentorProfileData}
        emrSectionsApiSignature={emrSectionsApiSignature}
        emrSettingsSaveApiSignature={emrSettingsSaveApiSignature}
      />
    </>
  )
}
